import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import findIndex from 'lodash/findIndex';
import posed, { PoseGroup } from 'react-pose';
import { keyframes, css } from '@emotion/core';

import config from 'config';
import Icon from 'common/icon';

const PopIn = posed.span({
    exit: { scale: 2, opacity: 0, transition: { duration: 200 } },
    enter: { scale: 1, opacity: 1, transition: { duration: 200 } },
});
const flashBorder = keyframes`
    from {
        background: ${config('/tailwind/colors/gray10')};
        border-color: ${config('/tailwind/colors/gray30')};
    }

    to {
        border-color: ${config('/tailwind/colors/green50')};
        background: ${config('/tailwind/colors/green30')};
    }
`;

const activeItem = css`
    animation: ${flashBorder} 1s ease alternate infinite;
`;

const ContributeProgress = ({ steps, current, onClick }) => {
    const currentIndex = findIndex(steps, { id: current });
    const segmentPercent = 100 / (steps.length - 1);
    const completedSegment = currentIndex * segmentPercent;
    const remainingSegment = 100 - completedSegment;

    return (
        <div className="flex flex-col justify-between relative" css={{ height: 400 }}>
            <div
                className="absolute pin-y pin-r my-4 z-0 overflow-hidden w-1"
                css={{ marginRight: 10 }}
            >
                <span
                    className="border-r-4 border-dotted border-gray-40 absolute pin-r transition h-full"
                    css={{ bottom: `${100 - completedSegment}%` }}
                />
                <span
                    className="border-r-4 border-dotted border-gray-20 absolute pin-r transition h-full"
                    css={{ bottom: `${(100 - remainingSegment) * -1}%` }}
                />
            </div>
            {steps.map((step, i) => {
                const isComplete = i < currentIndex;
                const isCurrent = step.id === current;

                return (
                    <button
                        key={step.id}
                        type="button"
                        onClick={() => onClick(step.id)}
                        className={cx(
                            'flex flex-row-reverse items-center text-16 font-medium relative z-10',
                            {
                                'text-gray-30': !isComplete && !isCurrent,
                                'text-gray-60': isComplete || isCurrent,
                                'pointer-events-none': !isComplete,
                            }
                        )}
                    >
                        <span
                            className={cx(
                                'inline-block w-8 h-8 border-3 rounded-full relative text-12',
                                {
                                    'border-gray-30 bg-gray-10': !isComplete && !isCurrent,
                                    'border-gray-60 bg-green-50': isComplete,
                                }
                            )}
                            css={isCurrent && activeItem}
                        >
                            <PoseGroup withParent={false}>
                                {isComplete && (
                                    <PopIn
                                        key="checkmark"
                                        className="flex justify-center items-center absolute pin w-full h-full text-white"
                                    >
                                        <Icon icon="check" type="solid" size="sm" />
                                    </PopIn>
                                )}
                            </PoseGroup>
                        </span>
                        <span className="pr-4">{step.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

ContributeProgress.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
        })
    ).isRequired,
    current: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
};

export default ContributeProgress;
